import { useState, useEffect } from "react"
import type React from "react"
import { Website, Speaker, AgendaRow } from "sharedTypes"
import {
  WebsiteFormValues,
  SpeakerFormValues,
  AgendaFormValues,
} from "../types"

type UseWebsitePreviewReturn = {
  previewWebsite: Website
  setPreviewWebsite: React.Dispatch<React.SetStateAction<Website>>
  updatePreview: (values: WebsiteFormValues) => void
  updateSpeakerPreview: (
    editingSpeaker: Speaker | null,
    tempRef: React.MutableRefObject<number | null>,
    values: SpeakerFormValues,
  ) => void
  updateAgendaRowPreview: (
    editingItem: AgendaRow | null,
    tempRef: React.MutableRefObject<number | null>,
    rowType: "AgendaItem" | "AgendaHeading",
    values: AgendaFormValues,
  ) => void
  resetPreview: () => void
}

/**
 * Hook for managing website preview state
 * Handles live preview updates for all website form changes
 */
export const useWebsitePreview = (website: Website): UseWebsitePreviewReturn => {
  const [previewWebsite, setPreviewWebsite] = useState(website)

  // Sync preview with website when website changes
  useEffect(() => {
    setPreviewWebsite(website)
  }, [website])

  // Simple update for most form changes
  const updatePreview = (values: WebsiteFormValues) => {
    setPreviewWebsite((prev) => ({ ...prev, ...values }))
  }

  // Update speaker preview (handles both editing and new speakers)
  const updateSpeakerPreview = (
    editingSpeaker: Speaker | null,
    tempRef: React.MutableRefObject<number | null>,
    values: SpeakerFormValues,
  ) => {
    if (editingSpeaker) {
      // Update existing speaker in preview
      setPreviewWebsite((prev) => ({
        ...prev,
        speakers: prev.speakers.map((s) => (
          s.id === editingSpeaker.id
            ? { ...s, ...values, photoUrl: values.photo || s.photoUrl }
            : s
        )),
      }))
    } else {
      // For new speakers, add or update a temporary one in the preview
      setPreviewWebsite((prev) => {
        // Get or create temp ID
        // eslint-disable-next-line no-param-reassign
        if (!tempRef.current) {
          // eslint-disable-next-line no-param-reassign
          tempRef.current = Date.now()
        }
        const currentTempId = tempRef.current

        const tempSpeaker = {
          id: currentTempId,
          ...values,
          photoUrl: values.photo || "",
          position: (prev.speakers.length > 0
            ? Math.max(...prev.speakers.map((s) => s.position || 0)) + 1
            : 1),
        } as Speaker

        // Check if temp speaker already exists
        const hasTempSpeaker = prev.speakers.some((s) => s.id === currentTempId)

        return {
          ...prev,
          speakers: hasTempSpeaker
            ? prev.speakers.map((s) => (s.id === currentTempId ? tempSpeaker : s))
            : [...prev.speakers, tempSpeaker],
        }
      })
    }
  }

  // Update agenda row preview (handles both editing and new rows)
  const updateAgendaRowPreview = (
    editingItem: AgendaRow | null,
    tempRef: React.MutableRefObject<number | null>,
    rowType: "AgendaItem" | "AgendaHeading",
    values: AgendaFormValues,
  ) => {
    if (editingItem) {
      // Update existing agenda row in preview
      setPreviewWebsite((prev) => ({
        ...prev,
        agendaRows: prev.agendaRows.map((row) => (
          row.id === editingItem.id
            ? { ...row, ...values, type: rowType }
            : row
        )),
      }))
    } else {
      // For new agenda rows, add or update a temporary one in the preview
      setPreviewWebsite((prev) => {
        // eslint-disable-next-line no-param-reassign
        if (!tempRef.current) {
          // eslint-disable-next-line no-param-reassign
          tempRef.current = Date.now()
        }
        const currentTempId = tempRef.current

        const tempRow = {
          id: currentTempId,
          ...values,
          type: rowType,
        } as AgendaRow

        const hasTempRow = prev.agendaRows.some((row) => row.id === currentTempId)

        return {
          ...prev,
          agendaRows: hasTempRow
            ? prev.agendaRows.map((row) => (row.id === currentTempId ? tempRow : row))
            : [...prev.agendaRows, tempRow],
        }
      })
    }
  }

  // Reset preview to original website state
  const resetPreview = () => {
    setPreviewWebsite(website)
  }

  return {
    previewWebsite,
    setPreviewWebsite,
    updatePreview,
    updateSpeakerPreview,
    updateAgendaRowPreview,
    resetPreview,
  }
}

export default useWebsitePreview
